import { useState, useMemo } from 'react';

// ── Aggregation helpers ───────────────────────────────────────
function aggregateKpi(kpi, endDate, n, useGoals) {
  const dates = kpi.dates || [];
  const dataObj = useGoals ? kpi.goals : kpi.data;
  const idx = dates.indexOf(endDate);
  if (idx < 0 || n <= 1) return (useGoals ? kpi.goals[endDate] : kpi.data[endDate]) ?? null;
  const window = dates.slice(Math.max(0, idx - n + 1), idx + 1);
  const vals = window.map(d => dataObj[d]).filter(v => v != null);
  if (!vals.length) return null;
  return kpi.periodicMeasure === 'Total'
    ? vals.reduce((s, v) => s + v, 0)
    : vals.reduce((s, v) => s + v, 0) / vals.length;
}


// ── Chart Modal ───────────────────────────────────────────────
function ChartModal({ kpi, selectedDate, graphPeriod, onClose, graphStartDate }) {
  const dates = kpi.dates || [];
  const rawDates = graphPeriod === 'Custom' && graphStartDate
    ? dates.filter(d => d >= graphStartDate && d <= (selectedDate || ''))
    : filterDatesByPeriod(dates, selectedDate, graphPeriod, kpi.frequency);
  // Sample down if too many points to avoid SVG freeze
  const step = rawDates.length > 104 ? Math.ceil(rawDates.length / 104) : 1;
  const visibleDates = rawDates.filter((_, i) => i % step === 0 || i === rawDates.length - 1);
  const points = visibleDates.map(d => ({
    date: d, label: formatDate(d, kpi.frequency),
    actual: kpi.data[d] ?? null, goal: kpi.goals[d] ?? null,
  })).filter(p => p.actual != null);

  const current = findNearestDate(dates, selectedDate);
  const actual = current ? (kpi.data[current] ?? null) : null;
  const goal   = current ? (kpi.goals[current] ?? null) : null;
  const variance = formatVariance(actual, goal, kpi.type, kpi.goodVsGoal);

  // Build SVG chart
  const W = 800, H = 320, PL = 64, PR = 24, PT = 20, PB = 40;
  const cW = W - PL - PR, cH = H - PT - PB;

  const vals = points.map(p => p.actual);
  const avgLine = vals.length > 1 ? vals.reduce((s,v)=>s+v,0)/vals.length : null;
  const goalVals = points.map(p => p.goal).filter(g => g != null);
  const allVals = [...vals, ...goalVals];
  let minV = Math.min(...allVals), maxV = Math.max(...allVals);
  const span = maxV - minV;
  if (span < Math.abs(maxV) * 0.05) { minV = minV * 0.9; maxV = maxV * 1.1; }
  const range = maxV - minV || Math.abs(maxV) * 0.2 || 1;

  const px = i => PL + (i / Math.max(points.length - 1, 1)) * cW;
  const py = v => PT + cH - ((v - minV) / range) * cH;

  const actualPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(p.actual).toFixed(1)}`).join(' ');

  // Prior year line — must be after px/py are defined
  const modalShowPY = ['L4P', 'YTD', 'T12M'].includes(graphPeriod);
  const modalPYData = modalShowPY ? points.map((p, i) => {
    const yr = parseInt(p.date.slice(0, 4));
    const target = (yr - 1) + p.date.slice(4);
    const v = kpi.data[target] ?? null;
    return v != null ? { xIdx: i, v } : null;
  }).filter(Boolean) : [];
  const modalPYPath = modalPYData.length > 1
    ? modalPYData.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.xIdx).toFixed(1)},${py(p.v).toFixed(1)}`).join(' ')
    : null;
  const areaPath = points.length > 1 ? actualPath + ` L${px(points.length-1).toFixed(1)},${PT+cH} L${PL},${PT+cH} Z` : '';
  const hasGoal = points.some(p => p.goal != null);
  const goalPath = hasGoal ? points.filter(p => p.goal != null).map((p, i, arr) => {
    const oi = points.indexOf(p);
    return `${i === 0 ? 'M' : 'L'}${px(oi).toFixed(1)},${py(p.goal).toFixed(1)}`;
  }).join(' ') : null;

  // Y-axis ticks
  const yTicks = 5;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) => minV + (range * i / yTicks));

  // X-axis labels — show max 8 evenly spaced
  const xStep = Math.max(1, Math.floor(points.length / 8));
  const xLabels = points.filter((_, i) => i % xStep === 0 || i === points.length - 1);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '90%', maxWidth: 900, padding: '28px 32px', boxShadow: '0 24px 80px rgba(10,22,40,0.3)', position: 'relative' }}>
        {/* Close button */}
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', border: '1px solid var(--border-strong)', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--text-muted)', fontFamily: 'inherit' }}>✕</button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, paddingRight: 40 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              {kpi.frequency === 'weekly' ? 'Weekly' : 'Monthly'} · {kpi.scope} · {graphPeriod}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{kpi.metric}</div>
          </div>
          <div style={{ display: 'flex', gap: 24, textAlign: 'right' }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Latest</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{formatValue(actual, kpi.type)}</div>
            </div>
            {goal != null && <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Goal</div>
              <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--text-secondary)' }}>{formatValue(goal, kpi.type)}</div>
            </div>}
            {variance.isGood !== null && <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Variance</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: variance.isGood ? '#1a5c2a' : '#8b1a1a' }}>{variance.label}</div>
            </div>}
          </div>
        </div>

        {/* SVG Chart */}
        {points.length < 2 ? (
          <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Not enough data for this period</div>
        ) : (
          <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
            <defs>
              <linearGradient id="modalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e5fd4" stopOpacity="0.15"/>
                <stop offset="100%" stopColor="#1e5fd4" stopOpacity="0"/>
              </linearGradient>
            </defs>
            {/* Grid lines */}
            {yTickVals.map((v, i) => (
              <g key={i}>
                <line x1={PL} y1={py(v)} x2={W-PR} y2={py(v)} stroke="#f0f0f0" strokeWidth="1"/>
                <text x={PL-8} y={py(v)+4} textAnchor="end" fontSize="11" fill="#aaa">{formatValue(v, kpi.type)}</text>
              </g>
            ))}
            {/* Area fill */}
            {areaPath && <path d={areaPath} fill="url(#modalGrad)"/>}
            {/* Goal line */}
            {goalPath && <path d={goalPath} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="5,4"/>}
            {/* Prior year line */}
            {modalPYPath && <path d={modalPYPath} fill="none" stroke="#93c5fd" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.8"/>}
            {/* Average line */}
            {avgLine !== null && <line x1={PL} y1={py(avgLine)} x2={W-PR} y2={py(avgLine)} stroke="#c9a84c" strokeWidth="1.5" strokeDasharray="6,4"/>}
            {/* Actual line */}
            <path d={actualPath} fill="none" stroke="#1e5fd4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            {/* Data point dots */}
            {points.map((p, i) => (
              <circle key={i} cx={px(i)} cy={py(p.actual)} r={i === points.length-1 ? 5 : 3}
                fill={i === points.length-1 ? '#1e5fd4' : 'white'}
                stroke="#1e5fd4" strokeWidth={i === points.length-1 ? 0 : 1.5}/>
            ))}
            {/* X-axis labels */}
            {xLabels.map((p, i) => (
              <text key={i} x={px(points.indexOf(p))} y={H-8} textAnchor="middle" fontSize="11" fill="#aaa">{p.label}</text>
            ))}
            {/* X axis line */}
            <line x1={PL} y1={PT+cH} x2={W-PR} y2={PT+cH} stroke="#e5e7eb" strokeWidth="1"/>
          </svg>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="20" height="8"><line x1="0" y1="4" x2="16" y2="4" stroke="#1e5fd4" strokeWidth="2.5"/><circle cx="16" cy="4" r="3" fill="#1e5fd4"/></svg>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Actual</span>
          </div>
          {hasGoal && <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="5,4"/></svg>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Goal</span>
          </div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#93c5fd" strokeWidth="1.5" strokeDasharray="4,3"/></svg>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prior year</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#c9a84c" strokeWidth="1.5" strokeDasharray="6,4"/></svg>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Period avg</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── SCV Logo SVG ──────────────────────────────────────────────
const SCVLogoSVG = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: 'block', flexShrink: 0 }}>
    <circle cx="50" cy="50" r="50" fill="white"/>
    <ellipse cx="50" cy="48" rx="44" ry="45" fill="none" stroke="#c0c0c0" strokeWidth="3" opacity="0.7"/>
    <ellipse cx="51" cy="51" rx="42" ry="43" fill="none" stroke="#d0d0d0" strokeWidth="2" opacity="0.5"/>
    <ellipse cx="50" cy="50" rx="40" ry="41" fill="none" stroke="#c9a84c" strokeWidth="2.5" opacity="0.95"/>
    <ellipse cx="49" cy="50" rx="37" ry="38" fill="none" stroke="#c0c0c0" strokeWidth="2" opacity="0.5"/>
    <line x1="52" y1="14" x2="52" y2="86" stroke="#b8b8b8" strokeWidth="1" opacity="0.8"/>
    <line x1="51" y1="14" x2="51" y2="86" stroke="#c0c0c0" strokeWidth="0.6" opacity="0.5"/>
    <line x1="53" y1="14" x2="53" y2="86" stroke="#c0c0c0" strokeWidth="0.6" opacity="0.5"/>
    <line x1="28" y1="38" x2="74" y2="38" stroke="#b8b8b8" strokeWidth="0.8" opacity="0.8"/>
    <line x1="28" y1="40" x2="74" y2="40" stroke="#b8b8b8" strokeWidth="0.8" opacity="0.7"/>
    <line x1="28" y1="42" x2="74" y2="42" stroke="#b0b0b0" strokeWidth="0.7" opacity="0.6"/>
    <line x1="28" y1="44" x2="74" y2="44" stroke="#b0b0b0" strokeWidth="0.6" opacity="0.5"/>
    <line x1="28" y1="46" x2="74" y2="46" stroke="#b0b0b0" strokeWidth="0.5" opacity="0.4"/>
    <line x1="28" y1="48" x2="74" y2="48" stroke="#b0b0b0" strokeWidth="0.5" opacity="0.3"/>
    <rect x="49.5" y="60" width="5" height="8" fill="none" stroke="#c0c0c0" strokeWidth="0.8" opacity="0.6"/>
  </svg>
);


// ── SCV Ring Logo — grey rings + gold accent, white bg ──
const SCVRing = ({ icon, size = 40 }) => (
  <div style={{ width: size, height: size, position: 'relative', flexShrink: 0, backgroundColor: 'white', borderRadius: '50%' }}>
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: 'block' }}>
      <circle cx="50" cy="50" r="48" fill="white" />
      <ellipse cx="50" cy="49" rx="42" ry="43" fill="none" stroke="#c8c8c8" strokeWidth="4" opacity="0.8" />
      <ellipse cx="51" cy="50" rx="39" ry="40" fill="none" stroke="#b0b0b0" strokeWidth="3" opacity="0.6" />
      <ellipse cx="50" cy="50" rx="36" ry="37" fill="none" stroke="#c9a84c" strokeWidth="2.5" opacity="0.95" />
      <ellipse cx="49" cy="51" rx="33" ry="34" fill="none" stroke="#c8c8c8" strokeWidth="2" opacity="0.4" />
    </svg>
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {icon}
    </div>
  </div>
);

// ── KPI Icons ─────────────────────────────────────────────────
function getKpiIcon(metric, type) {
  const m = (metric || '').toLowerCase();
  const iconProps = { width: 14, height: 14, fill: 'none', stroke: '#0a1628', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };

  if (m.includes('cash') || m.includes('balance')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M6 10h.01M2 10h20M6 6V4"/></svg>
  );
  if (m.includes('ar') || m.includes('receivable')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
  );
  if (m.includes('revenue') || m.includes('sales')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  );
  if (m.includes('gp') || m.includes('margin') || m.includes('utilization') || type === '%') return (
    <svg {...iconProps} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M8 12h8M12 8v8"/></svg>
  );
  if (m.includes('labor') || m.includes('employee') || m.includes('headcount')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  );
  if (m.includes('quoted') || m.includes('jobs') || m.includes('pipeline')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M9 12h6M9 16h4"/></svg>
  );
  if (m.includes('overtime') || m.includes('hours') || m.includes('ot')) return (
    <svg {...iconProps} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  );
  // Default: chart/trend
  return (
    <svg {...iconProps} viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
  );
}

// ── Formatting ────────────────────────────────────────────────
function formatValue(val, type) {
  if (val == null || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  if (type === '%') return (n * 100).toFixed(1) + '%';
  if (type === '#') {
    const hasDecimal = Math.abs(n % 1) > 0.001;
    return hasDecimal ? n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 }) : Math.round(n).toLocaleString();
  }
  if (type === 'ratio') return n.toFixed(2) + 'x';
  if (Math.abs(n) >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + Math.round(n).toLocaleString();
}

function formatVariance(actual, goal, type, goodVsGoal) {
  if (actual == null || goal == null) return { label: '—', isGood: null };
  const diff = actual - goal;
  const isGood = goodVsGoal === 'Less Than' ? diff <= 0 : diff >= 0;
  if (type === '%') return { label: (diff >= 0 ? '+' : '') + (diff * 100).toFixed(1) + 'pts', isGood };
  if (type === '#') {
    const hasDecimal = Math.abs(diff % 1) > 0.001;
    const diffStr = hasDecimal ? diff.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 }) : Math.round(diff).toLocaleString();
    return { label: (diff >= 0 ? '+' : '') + diffStr, isGood };
  }
  const abs = Math.abs(diff);
  const str = abs >= 1000000 ? '$' + (abs/1000000).toFixed(1)+'M' : abs >= 1000 ? '$'+Math.round(abs/1000)+'K' : '$'+Math.round(abs).toLocaleString();
  return { label: (diff >= 0 ? '+' : '-') + str, isGood };
}

function formatDate(dateStr, frequency) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (frequency === 'weekly') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function findNearestDate(dates, targetDate) {
  if (!dates || dates.length === 0) return null;
  if (!targetDate) return dates[dates.length - 1];
  const filtered = dates.filter(d => d <= targetDate);
  return filtered.length > 0 ? filtered[filtered.length - 1] : dates[dates.length - 1];
}

function getPriorPeriods(dates, currentDate, n) {
  const idx = dates.indexOf(currentDate);
  if (idx < 0) return [];
  return dates.slice(Math.max(0, idx - n), idx).reverse();
}

// ── Filter dates by graph period ──────────────────────────────
function filterDatesByPeriod(dates, selectedDate, period, frequency) {
  if (!dates || !selectedDate) return dates || [];
  const current = selectedDate;
  const currentYear = current.slice(0, 4);

  switch (period) {
    case 'L4P':
      const idx = dates.indexOf(current);
      return idx >= 0 ? dates.slice(Math.max(0, idx - 3), idx + 1) : dates.slice(-4);
    case 'YTD':
      return dates.filter(d => d >= `${currentYear}-01-01` && d <= current);
    case 'T12M': {
      const cutoff = new Date(current + 'T00:00:00');
      cutoff.setMonth(cutoff.getMonth() - 12);
      return dates.filter(d => d >= cutoff.toISOString().slice(0, 10) && d <= current);
    }
    case 'T24M': {
      const cutoff = new Date(current + 'T00:00:00');
      cutoff.setMonth(cutoff.getMonth() - 24);
      return dates.filter(d => d >= cutoff.toISOString().slice(0, 10) && d <= current);
    }
    case 'Custom':
      return dates.filter(d => d <= current);
    default:
      return dates.filter(d => d <= current).slice(-52);
  }
}

// ── Inline SVG Sparkline ──────────────────────────────────────
function InlineSparkline({ kpi, selectedDate, graphPeriod, graphStartDate }) {
  const allKpiDates = kpi.dates || [];
  const visibleDates = graphPeriod === 'Custom' && graphStartDate
    ? allKpiDates.filter(d => d >= graphStartDate && d <= (selectedDate || ''))
    : filterDatesByPeriod(allKpiDates, selectedDate, graphPeriod, kpi.frequency);
  const points = visibleDates.map(d => ({ d, v: kpi.data[d] ?? null, g: kpi.goals[d] ?? null })).filter(p => p.v != null);
  // Prior year line for L4P, YTD, T12M only
  const showPY = ['L4P', 'YTD', 'T12M'].includes(graphPeriod);
  const pyLineData = (() => {
    if (!showPY || !allKpiDates.length) return [];
    const result = [];
    // Sort dates once for binary-search style lookup
    const sortedDates = allKpiDates.slice().sort();
    points.forEach((p, i) => {
      // Target: same month/week but 1 year prior - pure string manipulation
      const yr = parseInt(p.d.slice(0, 4));
      const target = (yr - 1) + p.d.slice(4); // "2026-05-01" -> "2025-05-01"
      // Direct lookup first (monthly data will hit this)
      if (kpi.data[target] != null) {
        result.push({ xIdx: i, v: kpi.data[target] });
        return;
      }
      // For weekly: find closest date to target by numeric difference of date strings
      // ISO dates sort/compare correctly as strings for same-length comparison
      let best = null, bestDiff = Infinity;
      for (let j = 0; j < sortedDates.length; j++) {
        const d = sortedDates[j];
        // Convert ISO date string to days since epoch for numeric diff
        const dDays = (new Date(d + 'T12:00:00Z').getTime()) / 86400000;
        const tDays = (new Date(target + 'T12:00:00Z').getTime()) / 86400000;
        const diff = Math.abs(dDays - tDays);
        if (diff < bestDiff) { bestDiff = diff; best = d; }
        if (d > target && diff > bestDiff) break; // past target, getting farther
      }
      // Only use if within 15 days (for weekly) or 45 days (for monthly)
      const limit = kpi.frequency === 'weekly' ? 15 : 45;
      if (best && bestDiff <= limit && kpi.data[best] != null) {
        result.push({ xIdx: i, v: kpi.data[best] });
      }
    });
    return result;
  })();

  if (points.length < 2) return (
    <div style={{ width: '100%', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>No data</span>
    </div>
  );

  const W = 180, H = 54, PAD = 6;
  const vals = points.map(p => p.v);
  const sparkAvg = vals.length > 1 ? vals.reduce((s,v)=>s+v,0)/vals.length : null;
  const goalVals = points.map(p => p.g).filter(g => g != null);
  const pyVals = pyLineData.map(p => p.v).filter(v => v != null);
  const allVals = [...vals, ...goalVals, ...pyVals];
  let minV = Math.min(...allVals), maxV = Math.max(...allVals);
  const span = maxV - minV;
  if (span < Math.abs(maxV) * 0.05) { minV = minV * 0.9; maxV = maxV * 1.1; }
  const range = maxV - minV || Math.abs(maxV) * 0.2 || 1;

  const px = i => PAD + (i / Math.max(points.length - 1, 1)) * (W - PAD * 2);
  const py = v => H - PAD - ((v - minV) / range) * (H - PAD * 2);

  const actualPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(p.v).toFixed(1)}`).join(' ');
  const pyPath = pyLineData.length > 1
    ? pyLineData.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.xIdx).toFixed(1)},${py(p.v).toFixed(1)}`).join(' ')
    : null;
  const areaPath = actualPath + ` L${px(points.length-1).toFixed(1)},${H} L${PAD},${H} Z`;
  const hasGoal = points.some(p => p.g != null);
  const goalPath = hasGoal ? points.filter(p => p.g != null).map((p, i) => {
    const oi = points.indexOf(p);
    return `${i === 0 ? 'M' : 'L'}${px(oi).toFixed(1)},${py(p.g).toFixed(1)}`;
  }).join(' ') : null;

  const uid = kpi.metric.replace(/[^a-z0-9]/gi, '');

  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`g${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e5fd4" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#1e5fd4" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#g${uid})`} />
      {pyPath && <path d={pyPath} fill="none" stroke="#93c5fd" strokeWidth="1.2" strokeDasharray="3,2" opacity="0.8"/>}
      {goalPath && <path d={goalPath} fill="none" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3"/>}
      {sparkAvg !== null && <line x1={PAD} y1={py(sparkAvg)} x2={W-PAD} y2={py(sparkAvg)} stroke="#c9a84c" strokeWidth="1.2" strokeDasharray="4,3"/>}
      <path d={actualPath} fill="none" stroke="#1e5fd4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={px(points.length-1)} cy={py(points[points.length-1].v)} r="3" fill="#1e5fd4" stroke="white" strokeWidth="1.5"/>
    </svg>
  );
}

// ── KPI Row ───────────────────────────────────────────────────
function KpiRow({ kpi, selectedDate, index, graphPeriod, onExpand, graphStartDate }) {
  const dates = kpi.dates || [];
  const currentDate = findNearestDate(dates, selectedDate);
  // PP = prior period (1 back), PY = prior year same period
  const priorPeriod = getPriorPeriods(dates, currentDate, 1)[0] || null;
  const priorYearDate = (() => {
    if (!currentDate) return null;
    const d = new Date(currentDate + 'T00:00:00');
    if (kpi.frequency === 'weekly') d.setDate(d.getDate() - 364); // 52 weeks back
    else d.setFullYear(d.getFullYear() - 1);
    const target = d.toISOString().slice(0, 10);
    return findNearestDate(dates.filter(x => x <= target), target);
  })();
  const actual = currentDate ? (kpi.data[currentDate] ?? null) : null;
  const goal   = currentDate ? (kpi.goals[currentDate] ?? null) : null;
  const variance = formatVariance(actual, goal, kpi.type, kpi.goodVsGoal);
  const icon = getKpiIcon(kpi.metric, kpi.type);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '40px 160px 100px 86px 80px 76px 76px 76px 1fr',
      alignItems: 'center', minHeight: 58,
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      padding: '0 6px', transition: 'background 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8f9fb'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Ring icon */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <SCVRing icon={icon} size={36} />
      </div>

      {/* KPI Name */}
      <div style={{ padding: '8px 6px 8px 2px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{kpi.metric}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {kpi.frequency === 'weekly' ? 'Weekly' : 'Monthly'}
          {kpi.scope && kpi.scope !== 'Total' ? ` · ${kpi.scope}` : ''}
        </div>
      </div>

      {/* Latest */}
      <div style={{ padding: '4px 6px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ background: '#0a1628', borderRadius: 8, padding: '6px 10px', textAlign: 'center', boxShadow: '0 1px 4px rgba(10,22,40,0.2)', width: 84, boxSizing: 'border-box' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'white', letterSpacing: '-0.02em', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatValue(actual, kpi.type)}
          </div>
        </div>
      </div>

      {/* Goal */}
      <div style={{ padding: '0 6px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>{formatValue(goal, kpi.type)}</div>
      </div>

      {/* Variance */}
      <div style={{ padding: '0 5px', textAlign: 'center' }}>
        {variance.isGood === null
          ? <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>—</div>
          : <div style={{ display: 'inline-block', background: variance.isGood ? '#86efac' : '#991b1b', color: variance.isGood ? '#14532d' : '#fff', fontSize: 11, fontWeight: 700, padding: '2px 6px', borderRadius: 5 }}>{variance.label}</div>
        }
      </div>

      {/* PP — Prior Period */}
      <div style={{ padding: '0 5px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
          {formatValue(priorPeriod ? (kpi.data[priorPeriod] ?? null) : null, kpi.type)}
        </div>
      </div>

      {/* PY — Prior Year */}
      <div style={{ padding: '0 5px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
          {formatValue(priorYearDate ? (kpi.data[priorYearDate] ?? null) : null, kpi.type)}
        </div>
      </div>

      {/* Y/Y % — Year over Year change */}
      <div style={{ padding: '0 5px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: (() => {
          if (actual == null || !priorYearDate || kpi.data[priorYearDate] == null) return 'var(--text-muted)';
          const pyv = kpi.data[priorYearDate];
          const chg = (actual - pyv) / Math.abs(pyv);
          const isGood = kpi.goodVsGoal === 'Less Than' ? chg <= 0 : chg >= 0;
          return isGood ? '#1a5c2a' : '#8b1a1a';
        })() }}>
          {(() => {
            if (actual == null || !priorYearDate || kpi.data[priorYearDate] == null) return '—';
            const pyv = kpi.data[priorYearDate];
            const chg = (actual - pyv) / Math.abs(pyv);
            return (chg >= 0 ? '+' : '') + (chg * 100).toFixed(1) + '%';
          })()}
        </div>
      </div>

      {/* Sparkline — click to expand */}
      <div onClick={onExpand} style={{ padding: '2px 8px 2px 4px', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        title="Click to expand chart"
      >
        <InlineSparkline kpi={kpi} selectedDate={currentDate} graphPeriod={graphPeriod} graphStartDate={graphStartDate} />

      </div>
    </div>
  );
}

// ── Table Header ──────────────────────────────────────────────
function TableHeader({ graphPeriod, setGraphPeriod, tabName, onPrint, companyName, periodSubtitle, graphStartDate, setGraphStartDate }) {
  const periods = ['L4P', 'YTD', 'T12M', 'T24M', 'Custom'];
  return (
    <>
      {/* Print-only header — hidden on screen */}
      <div className="print-header" style={{ alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderRadius: 12, background: '#fff', border: '1px solid #e5e7eb', marginBottom: 12 }}>
        {/* Left: logo + SCV name + company name — matches screen header exactly */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <SCVLogoSVG size={40} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 400, color: '#888' }}>Seven Circles Ventures</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f1923', letterSpacing: '-0.02em', marginTop: 2 }}>{companyName}</div>
          </div>
        </div>
        {/* Right: Tab title + subtitle */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0a1628', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{tabName}</div>
          {periodSubtitle && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{periodSubtitle}</div>}
        </div>
      </div>

      {/* Tab title bar — screen only */}
      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px 12px', background: '#fff', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'center', flex: 2 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {tabName}
          </div>
          {periodSubtitle && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.02em' }}>
              {periodSubtitle}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
      </div>
      {/* Column headers row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '40px 160px 100px 86px 80px 76px 76px 76px 1fr',
        background: 'var(--bg)', padding: '10px 8px', alignItems: 'center',
        borderBottom: '2px solid var(--navy)',
      }}>
        {['', 'Metric', 'Latest', 'Goal', 'Var.', 'PP', 'PY', 'Y/Y %'].map((h, i) => (
          <div key={i} style={{ 
            fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', 
            textAlign: i === 0 ? 'center' : i === 1 ? 'left' : 'center', 
            padding: '0 5px', letterSpacing: '0.08em', textTransform: 'uppercase' 
          }}>{h}</div>
        ))}
        {/* Graph range selector */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#8a6a1a', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {graphPeriod === 'Custom' && graphStartDate
              ? new Date(graphStartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) + ' – now'
              : graphPeriod}
          </div>
          <div style={{ display: 'flex', gap: 2 }}>
            {periods.map(per => (
              <button key={per} onClick={() => { setGraphPeriod(per); if (per !== 'Custom') setGraphStartDate(null); }} style={{
                fontSize: 8, fontWeight: 700, padding: '2px 4px', borderRadius: 4, cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s',
                border: graphPeriod === per ? '2px solid #c9a84c' : '1px solid var(--border-strong)',
                background: graphPeriod === per ? 'rgba(201,168,76,0.1)' : 'transparent',
                color: graphPeriod === per ? '#8a6a1a' : 'var(--text-secondary)',
              }}>{per}</button>
            ))}
          </div>
          {graphPeriod === 'Custom' && (
            <input type="date" value={graphStartDate || ''} onChange={e => setGraphStartDate(e.target.value)}
              style={{ fontSize: 9, padding: '2px 4px', borderRadius: 4, border: '1px solid #c9a84c', fontFamily: 'inherit', outline: 'none', width: 108 }} />
          )}
        </div>
      </div>
    </>
  );
}

// ── Date Picker ───────────────────────────────────────────────
function PeriodPicker({ tabs, activeTabIdx, selectedDate, onDateChange }) {
  const tab = tabs[activeTabIdx];
  if (!tab) return null;
  const allDates = new Set();
  tab.kpis.forEach(kpi => (kpi.dates || []).forEach(d => allDates.add(d)));
  const sortedDates = [...allDates].sort();
  if (!sortedDates.length) return null;
  const maxDate = sortedDates[sortedDates.length - 1];

  function handleInput(e) {
    const val = e.target.value; if (!val) return;
    const filtered = sortedDates.filter(d => d <= val);
    onDateChange(filtered.length > 0 ? filtered[filtered.length - 1] : maxDate);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>Period</span>
      <input type="date" value={selectedDate || maxDate} min={sortedDates[0]} max={maxDate} onChange={handleInput}
        style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: '#fff', color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit', outline: 'none' }} />
      {selectedDate && selectedDate !== maxDate && (
        <button onClick={() => onDateChange(maxDate)} style={{ fontSize: 11, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'transparent', cursor: 'pointer', color: 'var(--accent)', fontFamily: 'inherit', fontWeight: 600 }}>Latest</button>
      )}
    </div>
  );
}


// ── Valuation Cards (Executive tab only) ─────────────────────
function ValuationSection({ templateData, companySettings, multiples }) {
  if (!multiples || !templateData) return null;

  // Get TTM EBITDA from monthly data - look for EBITDA in any tab's KPIs
  const ebitdaKpi = (() => {
    for (const tab of templateData.tabs || []) {
      const kpi = tab.kpis.find(k => k.metric.toLowerCase().includes('ebitda'));
      if (kpi) return kpi;
    }
    return null;
  })();

  // Also check monthly sheet directly via templateData.monthly
  const monthlyData = templateData.monthly || {};
  let ttmEbitda = null;
  if (ebitdaKpi) {
    // 3-year average EBITDA: sum last 36 months divided by 3
    const allDates = (ebitdaKpi.dates || []).slice().sort();
    const last36 = allDates.slice(-36);
    const vals = last36.map(d => ebitdaKpi.data[d]).filter(v => v != null);
    if (vals.length > 0) {
      const totalYears = Math.max(1, Math.floor(vals.length / 12));
      ttmEbitda = vals.reduce((s, v) => s + v, 0) / totalYears;
    }
  }

  // If no EBITDA KPI found, show placeholder
  if (!ttmEbitda) return (
    <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Implied Valuation</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>· Add an EBITDA KPI to your Executive tab to enable valuation cards</div>
    </div>
  );

  // Apply deductions
  let totalDeduction = 0;
  if (companySettings?.owner_dependent) totalDeduction += 0.5;
  if (companySettings?.recurring_revenue_pct != null && companySettings.recurring_revenue_pct < 25) totalDeduction += 0.5;
  if (companySettings?.revenue_predictable === false) totalDeduction += 0.5;
  if (companySettings?.financial_visibility === false) totalDeduction += 0.5;
  if (companySettings?.capex_pct != null && companySettings.capex_pct > 2.5) totalDeduction += 0.5;

  const adjMid = Math.max(1, (multiples.multiple_mid || 4) - totalDeduction);
  const adjLow = Math.max(1, (multiples.multiple_low || 3) - totalDeduction);
  const adjHigh = Math.max(1, (multiples.multiple_high || 5) - totalDeduction);
  const valMid = Math.round(ttmEbitda * adjMid);
  const updatedYear = multiples.updated_at ? new Date(multiples.updated_at).getFullYear() : 2026;

  const fmt$ = n => {
    if (n == null) return '—';
    if (Math.abs(n) >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1000) return '$' + Math.round(n / 1000) + 'K';
    return '$' + Math.round(n).toLocaleString();
  };

  return (
    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: '#fafafa' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Implied Valuation</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        {/* TTM EBITDA */}
        <div style={{ background: '#0a1628', borderRadius: 10, padding: '12px 16px', boxShadow: '0 4px 12px rgba(10,22,40,0.2)' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 4, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>3-Year Avg EBITDA</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>{fmt$(ttmEbitda)}</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>3-year annual average</div>
        </div>
        {/* Multiple */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{multiples.industry} · Multiple</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{adjMid.toFixed(1)}x</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Updated {updatedYear}</div>
        </div>
        {/* Implied EV */}
        <div style={{ background: '#eef7f1', borderRadius: 10, padding: '12px 16px', border: '1px solid rgba(26,122,60,0.15)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 10, color: '#1a7a3c', marginBottom: 4, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Implied Enterprise Value</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{fmt$(valMid)}</div>

        </div>
      </div>
    </div>
  );
}

// ── Main TabDashboard ─────────────────────────────────────────
export default function TabDashboard({ templateData, slug, user, multiples: propMultiples, companySettings: propSettings }) {
  const tabs = templateData?.tabs || [];
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [graphPeriod, setGraphPeriod] = useState('T12M');
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [graphStartDate, setGraphStartDate] = useState(null);
  const multiples = propMultiples || null;
  const companySettings = propSettings || null;

  const defaultDate = useMemo(() => {
    const tab = tabs[activeTabIdx];
    if (!tab) return null;
    const allDates = new Set();
    tab.kpis.forEach(kpi => Object.keys(kpi.data || {}).forEach(d => allDates.add(d)));
    const sorted = [...allDates].sort();
    return sorted[sorted.length - 1] || null;
  }, [tabs, activeTabIdx]);

  const effectiveDate = selectedDate || defaultDate;

  function handleTabSwitch(idx) { setActiveTabIdx(idx); setSelectedDate(null); setGraphStartDate(null); }

  if (!tabs.length) return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)' }}>No dashboard tabs configured</div>
      <div style={{ fontSize: 12 }}>Upload the SCV template to populate this dashboard</div>
    </div>
  );

  const activeTab = tabs[activeTabIdx];

  return (
    <>
    <div>
      {/* Tab nav + date picker + print */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <select value={activeTabIdx} onChange={e => handleTabSwitch(parseInt(e.target.value))} style={{
          fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8,
          border: '1px solid var(--border-strong)', background: '#fff',
          color: 'var(--text-primary)', cursor: 'pointer', fontFamily: 'inherit', outline: 'none',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
        }}>
          {tabs.map((tab, idx) => (
            <option key={idx} value={idx}>{tab.name}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PeriodPicker tabs={tabs} activeTabIdx={activeTabIdx} selectedDate={effectiveDate} onDateChange={setSelectedDate} />

        <button onClick={() => window.print()} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: '#fff', cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'inherit' }}>
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><rect x="3" y="1" width="8" height="6" rx="1"/><path d="M3 9H1V5h12v4h-2"/><rect x="3" y="8" width="8" height="5" rx="1"/><circle cx="11" cy="7" r="0.7" fill="currentColor"/></svg>
          Print
        </button>
      </div>
      </div>

      {/* KPI table */}
      <div id="scv-print-area" style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        {/* Valuation section — first tab only */}
        {activeTabIdx === 0 && (
          <ValuationSection templateData={templateData} companySettings={companySettings} multiples={multiples} />
        )}
        <TableHeader
          graphPeriod={graphPeriod}
          setGraphPeriod={setGraphPeriod}
          graphStartDate={graphStartDate}
          setGraphStartDate={setGraphStartDate}
          tabName={activeTab.name}
          companyName={templateData?.companyName || ''}
          onPrint={() => { window.print(); }}
          periodSubtitle={(() => {
            if (!effectiveDate) return null;
            const freq = activeTab.kpis[0]?.frequency;
            const d = new Date(effectiveDate + 'T00:00:00');
            return freq === 'weekly'
              ? 'Week of ' + d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
              : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          })()}
        />
        <div style={{ background: '#fff' }}>
          {activeTab.kpis.map((kpi, i) => (
            <KpiRow key={i} kpi={kpi} selectedDate={effectiveDate} index={i} graphPeriod={graphPeriod} onExpand={() => setSelectedKpi(kpi)} graphStartDate={graphStartDate} />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ marginTop: 10, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="20" height="8"><line x1="0" y1="4" x2="16" y2="4" stroke="#1e5fd4" strokeWidth="1.5"/><circle cx="16" cy="4" r="2.5" fill="#1e5fd4" stroke="white" strokeWidth="1"/></svg>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Actual</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3"/></svg>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Goal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="20" height="8"><line x1="0" y1="4" x2="20" y2="4" stroke="#93c5fd" strokeWidth="1" strokeDasharray="2,2"/></svg>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Prior year</span>
        </div>

      </div>
    </div>

    {/* KPI Chart Modal */}
    {selectedKpi && (
      <ChartModal kpi={selectedKpi} selectedDate={effectiveDate} graphPeriod={graphPeriod} onClose={() => setSelectedKpi(null)} graphStartDate={graphStartDate} />
    )}
    </>
  );
}

